import { useEffect, useRef, useState } from 'react';
import type { SessionInfo } from '@term-deck/core/types';
import type { SessionModelSwitch } from '@term-deck/core/session-model-switch.js';
import { MODEL_IDS, MODEL_LABELS } from '@term-deck/core/model.js';
import {
  CODEX_MODEL_IDS,
  CODEX_MODEL_LABELS,
  codexEffortLevelsForModel,
  normalizeCodexModel,
  type CodexEffortLevel,
} from '@term-deck/core/codex-model.js';
import { modelSwitchPending } from '@term-deck/core/session-model-switch.js';
import { api } from '../api/client';
import { ModalSurface } from './ModalSurface';
import './session-model-control.css';

const phases: Record<SessionModelSwitch['phase'], string> = {
  waiting: 'Aguardando o terminal ficar livre',
  interrupting: 'Interrompendo o turno em voo',
  saving_state: 'Salvando o estado no lote',
  compacting: 'Compactando o contexto',
  effort_command: 'Aplicando o esforço',
  model_picker: 'Selecionando o modelo',
  effort_picker: 'Selecionando o esforço',
  verifying: 'Confirmando a troca',
  applied: 'Modelo confirmado nesta sessão',
  blocked: 'Troca não confirmada · sessão preservada',
};
const reasons: Record<string, string> = {
  native_session_unverified: 'A identidade da conversa ainda não foi confirmada.',
  waiting_safe_boundary: 'O terminal precisa estar ocioso, sem texto no campo de entrada.',
  paused: 'O terminal está pausado.',
  newer_human_instruction: 'Uma nova instrução sua teve prioridade.',
  human_input_took_precedence: 'Sua entrada teve prioridade.',
  server_restarted_origin_preserved: 'O servidor reiniciou; a conversa foi preservada.',
  verification_timeout_origin_preserved: 'A CLI não confirmou a operação no prazo.',
  native_session_changed: 'A identidade da conversa mudou.',
  target_not_visible_in_model_picker: 'O modelo não aparece no seletor desta CLI.',
  target_not_visible_in_effort_picker: 'O esforço não aparece no seletor desta CLI.',
  global_preference_changed_concurrently: 'A configuração global foi editada durante a troca.',
};

export function SessionModelControl({ session }: { session: SessionInfo }) {
  const current = session.provider === 'codex' ? session.codexModel : session.model;
  const [open, setOpen] = useState(false),
    [model, setModel] = useState<string>(current ?? '');
  const [effort, setEffort] = useState<CodexEffortLevel | ''>('');
  const [transition, setTransition] = useState<SessionModelSwitch | null>(null),
    [error, setError] = useState<string | null>(null),
    [loading, setLoading] = useState(true),
    [saving, setSaving] = useState(false);
  const requestRevision = useRef(0);
  useEffect(() => {
    setModel(current ?? '');
    setEffort('');
  }, [current, session.id]);
  useEffect(() => {
    if (!open) return;
    let disposed = false;
    setLoading(true);
    setError(null);
    const refresh = () => {
      const revision = requestRevision.current;
      void api
        .getSessionModelSwitch(session.id)
        .then((r) => {
          if (!disposed && revision === requestRevision.current) {
            setTransition(r.transition);
            setError(null);
          }
        })
        .catch(() => {
          if (!disposed) setError('Não foi possível consultar a troca.');
        })
        .finally(() => {
          if (!disposed) setLoading(false);
        });
    };
    refresh();
    const timer = window.setInterval(refresh, 2500);
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, [open, session.id]);
  if (session.provider !== 'claude' && session.provider !== 'codex') return null;
  const pending = modelSwitchPending(transition);
  const models = session.provider === 'codex' ? CODEX_MODEL_IDS : MODEL_IDS;
  const effortLevels = codexEffortLevelsForModel(normalizeCodexModel(model));
  const validEffort = !effort || effortLevels.some((value) => value === effort);
  return (
    <span className="session-model-control">
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        Trocar modelo
      </button>
      {open && (
        <ModalSurface
          title="Trocar modelo nesta sessão"
          onClose={() => setOpen(false)}
          className="modal session-model-control-panel"
        >
          <p>
            O contexto será compactado antes da troca. A conversa e o terminal permanecem abertos.
          </p>
          <label>
            Modelo{' '}
            <select
              aria-label="Modelo nesta sessão"
              value={model}
              disabled={loading || pending || saving}
              onChange={(e) => setModel(e.target.value)}
            >
              {!model && <option value="">Selecione</option>}
              {models.map((value) => (
                <option key={value} value={value}>
                  {value in MODEL_LABELS
                    ? MODEL_LABELS[value as keyof typeof MODEL_LABELS]
                    : CODEX_MODEL_LABELS[value as keyof typeof CODEX_MODEL_LABELS]}
                </option>
              ))}
            </select>
          </label>
          {session.provider === 'codex' && (
            <label>
              Esforço{' '}
              <select
                aria-label="Esforço nesta sessão"
                value={effort}
                disabled={loading || pending || saving}
                onChange={(event) => setEffort(event.target.value as CodexEffortLevel | '')}
              >
                <option value="">Manter o atual ({session.codexEffort ?? 'high'})</option>
                {effortLevels.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
              <small>Vale só para esta conversa. O padrão do projeto permanece igual.</small>
            </label>
          )}
          {!validEffort && (
            <p role="alert">Escolha um esforço compatível com o modelo selecionado.</p>
          )}
          <button
            type="button"
            className="session-model-submit"
            disabled={loading || !model || !validEffort || pending || saving}
            onClick={() => {
              setSaving(true);
              requestRevision.current++;
              setError(null);
              void api
                .switchSessionModel(
                  session.id,
                  model,
                  session.provider === 'codex' && effort ? effort : undefined,
                )
                .then((r) => setTransition(r.transition ?? null))
                .catch((e) =>
                  setError(e instanceof Error ? e.message : 'Não foi possível solicitar a troca.'),
                )
                .finally(() => setSaving(false));
            }}
          >
            {saving ? 'Solicitando…' : 'Compactar e trocar'}
          </button>
          {loading && <p role="status">Consultando o estado desta sessão…</p>}
          {!loading && transition && (
            <p role="status">
              {phases[transition.phase]}
              {transition.reason && (
                <small>
                  {' '}
                  {reasons[transition.reason] ??
                    'A operação aguarda verificação. Sua sessão permanece aberta.'}
                </small>
              )}
            </p>
          )}
          {error && <p role="alert">{error}</p>}
          <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
            Fechar
          </button>
        </ModalSurface>
      )}
    </span>
  );
}
